import React from "react";
import { StatusCell } from "../WorkbenchStatusBar";
import { formatCurrency } from "../../../utils/formatters";
import { JOB_LABELS } from "./traceLabels";
import { toNumberOrNull } from "./qboLine";
import { positiveJobLaborBurdenOverride } from "../../../utils/jobPayload";

/**
 * The bottom line of the estimate screen.
 *
 * Shop hours read `total_estimated_fab_hours` ALONE. The backend already
 * accumulates every department into that total (it adds the per-line
 * `labor_estimate_hours`, which is the department sum), so adding CNC, cut and
 * paint on top — as the old dashboard did — double counts the shop.
 *
 * Two things here exist because a replay of twelve real jobs found them
 * missing, each worth a double-digit percentage of the quote:
 *
 *   1. A line the backend refused to price contributes $0. The job total is
 *      then a plausible-looking number that is simply wrong, so when any line
 *      carries an error the quote cell stops showing a number at all and says
 *      how many lines are missing from it.
 *   2. Which labor rate is active is a 17%-of-labor decision that used to be
 *      invisible until you opened Pricing Factors. It now gets a cell whenever
 *      it is NOT the ratified shop rate — a legacy island/custom rate, or a
 *      per-job burden override. The ordinary shop rate is a global constant
 *      that never changes per job, so it rides in the Shop hours tooltip
 *      rather than spending a cell on it for a whole session.
 */

/* Every steady-state cell is `essential`. The strip is four cells wide now —
   build cost, profit, shop hours, quote — and all four fit at 768px, so the
   narrow-width cut has nothing left to drop. Dropping profit and shop hours
   there used to leave an iPad reading two numbers where a desktop read four.
   The cut still exists for the conditional cells (pricing/next, labor,
   materials), which arrive on top of the four and only when something is off;
   warn cells are kept essential by StatusCell itself. */

const STALE_TITLE =
  "From the last pricing run — inputs changed since. Recalculating.";

function money(value) {
  return value === null || value === undefined ? "—" : formatCurrency(value);
}

function hours(value) {
  const parsed = toNumberOrNull(value);
  return parsed === null ? "—" : `${parsed.toFixed(1)} hr`;
}

/** How many lines the backend refused to price. */
export function countFailedLines(results) {
  const lines = results?.line_item_results;
  if (!Array.isArray(lines)) return 0;
  return lines.filter((line) => Boolean(line?.error)).length;
}

/** Material failures stay in totals because their shop labor still priced. */
export function countUnresolvedMaterials(results) {
  const lines = results?.line_item_results;
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((count, line) => {
    const parentCount = line?.material_error ? 1 : 0;
    const childCount = Array.isArray(line?.children)
      ? line.children.filter((child) => Boolean(child?.material_error)).length
      : 0;
    return count + parentCount + childCount;
  }, 0);
}

/**
 * The labor rate the calculation is actually using.
 *
 * Since the labor-rate-type retirement (2026-08-18) every new estimate prices
 * shop hours at the burden rate — the labor cost per employee-hour — and that
 * is what this cell names. A JobVersion saved before the retirement can still
 * carry `island` or `custom`, and the engine still honors them, so those are
 * shown in warning tone: an Island charge-out rate run through overhead,
 * profit and commission double-bills labor, and a stale custom rate silently
 * under-costs every hour on the job.
 */
export function describeActiveLaborRate(formData) {
  const override = positiveJobLaborBurdenOverride(
    formData?.job_labor_burden_override,
  );
  if (override != null) {
    return {
      type: "job_override",
      label: `$${Number(override)}/hr (job override)`,
      warn: true,
      title:
        "Admin per-job burden override — this estimate is NOT using the ratified global burden rate.",
    };
  }
  const type = formData?.laborRateType || "burden";
  const rate = toNumberOrNull(
    type === "island"
      ? formData?.island_millwork_labor_rate
      : type === "custom"
        ? formData?.labor_custom_rate
        : formData?.labor_burden_rate,
  );
  const legacy = type === "island" || type === "custom";
  const name = legacy
    ? type === "island"
      ? "Island rate (legacy)"
      : "Custom rate (legacy)"
    : "Shop labor rate";
  return {
    type,
    label: rate === null ? name : `${name} $${Number(rate)}/hr`,
    warn: legacy,
    title: legacy
      ? "This estimate was saved under the retired labor-rate-type selector and still prices labor at that rate rather than the shop labor (burden) rate. Start it fresh, or use the admin per-job burden override, to cost it at shop labor."
      : "Every shop hour on this job is costed at the shop labor rate — the burden cost per employee-hour, set in Pricing Settings.",
  };
}

function EstimateStatusStrip({
  results = null,
  resultsStale = false,
  hint = null,
  formData = null,
}) {
  const stale = Boolean(resultsStale && results);
  const failedLines = countFailedLines(results);
  const unresolvedMaterials = countUnresolvedMaterials(results);
  const labor = describeActiveLaborRate(formData || {});
  const warnShown = Boolean(
    labor.warn || unresolvedMaterials > 0 || failedLines > 0,
  );
  const trialSummary = results?.linked_pricing_summary || null;

  return (
    <>
      {/* A problem indicator, not a coaching line. `hint` is null in every
          steady state, so this cell appears only when something is off — or
          when the figures below it are from a superseded run. */}
      {stale || hint ? (
        <StatusCell
          essential
          label={stale ? "Pricing" : "Next"}
          value={stale ? "Recalculate" : hint}
          title={
            stale
              ? "Inputs changed since the last pricing run."
              : hint || undefined
          }
        />
      ) : null}
      {/* The ordinary shop rate is a global constant — it lives in the Shop
          hours tooltip. A rate that is NOT the ratified one still costs a
          cell, because it silently moves every labor dollar on the job. */}
      {labor.warn ? (
        <StatusCell
          label="Labor"
          value={labor.label}
          tone="warn"
          title={labor.title}
        />
      ) : null}
      {/* At narrow widths only essential cells fit; a warning cell must never
          be the one pushed past the edge, so the two context cells give way
          whenever one is showing. */}
      {/* Stale figures are still the last run's numbers, but every per-line
          figure was just dropped for the same reason — so the totals dim
          rather than sit there looking as sure as fresh ones. */}
      <StatusCell
        essential
        label={JOB_LABELS.buildCost}
        value={money(results?.total_job_costs_P91)}
        tone={stale ? "stale" : undefined}
        title={stale ? STALE_TITLE : undefined}
      />
      <StatusCell
        essential={!warnShown}
        label={JOB_LABELS.profit}
        value={money(results?.profit_P97)}
        tone={stale ? "stale" : undefined}
        title={stale ? STALE_TITLE : undefined}
      />
      <StatusCell
        essential={!warnShown}
        label="Shop hours"
        value={hours(results?.total_estimated_fab_hours)}
        tone={stale ? "stale" : undefined}
        title={
          stale
            ? STALE_TITLE
            : trialSummary
              ? `Every department's hours, already totalled by the backend. ${hours(
                  trialSummary.job_rate_hours,
                )} costed at the ${labor.label}; ${hours(
                  trialSummary.trial_hours,
                )} are rafter-tail trial labor priced by the trial.`
              : `Every department's hours, already totalled by the backend. Costed at the ${labor.label}.`
        }
      />
      {unresolvedMaterials > 0 ? (
        <StatusCell
          label="Materials"
          value={`${unresolvedMaterials} material${
            unresolvedMaterials === 1 ? "" : "s"
          } unresolved`}
          tone="warn"
          title="These lines remain in the job total because shop labor priced, but their unresolved material contributed no material cost."
        />
      ) : null}
      {failedLines > 0 ? (
        <StatusCell
          label={`${JOB_LABELS.quote} — incomplete`}
          value={`Total excludes ${failedLines} failed line${
            failedLines === 1 ? "" : "s"
          }`}
          tone="warn"
          emphasis
          title="One or more lines did not price. Fix them before quoting — the total below them is not the job."
        />
      ) : (
        <StatusCell
          essential
          label={JOB_LABELS.quote}
          value={money(results?.final_suggested_sale_price_P105)}
          emphasis
          tone={stale ? "stale" : undefined}
          title={stale ? STALE_TITLE : undefined}
        />
      )}
    </>
  );
}

export default EstimateStatusStrip;
